import {
  useShowContext,
  Labeled,
  FunctionField,
  useGetOne,
  ReferenceField,
  useList,
  ListContextProvider,
  Datagrid,
  TextField,
} from "react-admin";
import { Box, Rating } from "@mui/material";
import UserNameField from "./user-name-field";

const metaRatings = {
  sparseFields: [
    "id",
    "rating",
    {
      ratings: ["id", "clientId", "providerId", "createdTimestamp", "rating"],
    },
  ],
};

const metaReviews = {
  sparseFields: [
    "id",
    {
      reviews: [
        "id",
        "clientId",
        "clientUserName",
        "createdTimestamp",
        "providerId",
        "review",
      ],
    },
  ],
};

const RatingsReviewsField = () => {
  const { record, resource } = useShowContext();
  const id = record.id;

  const {
    data: providerRatings,
    isLoading: isGetOneLoadingRatings,
    error: errorRatings,
  } = useGetOne(resource, { id, meta: metaRatings });

  const {
    data: providerReviews,
    isLoading: isGetOneLoadingReviews,
    error: errorReviews,
  } = useGetOne(resource, { id, meta: metaReviews });

  const getReviewForRating = (clientId) => {
    const review = providerReviews.reviews?.find(
      (r) => r.clientId === clientId,
    );
    return review?.review ?? "-";
  };

  const ratingListContext = useList({
    data: (providerRatings ? providerRatings.ratings : []).filter(
      (item) => item.clientId !== "1" && item.clientId !== 1,
    ),
    resource: "Rating",
  });

  if (isGetOneLoadingRatings || isGetOneLoadingReviews) {
    return <div>Loading...</div>;
  }

  if (errorRatings) {
    throw errorRatings;
  }

  if (errorReviews) {
    throw errorReviews;
  }

  const defaultRating = 5;
  const averageProviderRating = parseFloat(
    providerRatings ? providerRatings.rating : 0,
  );
  const averageRating = Number.isFinite(averageProviderRating)
    ? averageProviderRating
    : defaultRating;

  return (
    <Labeled sx={{ width: "100%" }} label="Ratings">
      <ListContextProvider value={ratingListContext}>
        <Datagrid
          bulkActionButtons={false}
          sx={{ "& thead": { display: "none" } }}
          rowSx={(_, index) => ({
            td: { padding: index > 0 ? "8px 0" : "0 0 8px" },
          })}
          rowClick={false}
        >
          <Box
            sx={{
              display: "grid",
              gap: "8px 32px",
              gridTemplateColumns: "auto auto 1fr auto",
            }}
          >
            <Labeled label="Customer ID">
              <TextField source="clientId" />
            </Labeled>

            <Labeled label="Customer Name">
              <ReferenceField
                source="clientId"
                reference="Customer"
                link="show"
              >
                <UserNameField />
              </ReferenceField>
            </Labeled>

            <Labeled label="Added on">
              <FunctionField
                render={(value) =>
                  value["createdTimestamp"]
                    ? new Date(+value["createdTimestamp"]).toDateString()
                    : "-"
                }
              />
            </Labeled>

            <Labeled
              label="Rating"
              component="div"
              sx={{ gridColumn: "span 3" }}
            >
              <FunctionField
                render={(value) => (
                  <Box
                    sx={{ width: 200, display: "flex", alignItems: "center" }}
                  >
                    <Rating
                      name="half-rating"
                      value={parseFloat(value.rating)}
                      precision={0.5}
                      readOnly
                    />
                    <Box sx={{ ml: 2 }}>{`${value.rating}/5`}</Box>
                  </Box>
                )}
              />
            </Labeled>
            <Labeled
              label="Review"
              component="div"
              sx={{ gridColumn: "span 3" }}
            >
              <FunctionField
                source="reviews.review"
                render={(value) => getReviewForRating(value.clientId)}
              />
            </Labeled>
          </Box>
        </Datagrid>

        <Box sx={{ padding: "16px 0", display: "flex", alignItems: "center" }}>
          <Box sx={{ mr: 2 }}>Average Rating: </Box>
          <Rating
            name="half-rating"
            value={parseFloat(averageRating)}
            precision={0.1}
            readOnly
          />
          <Box sx={{ ml: 2 }}>{`${averageRating}/5`}</Box>
        </Box>
      </ListContextProvider>
    </Labeled>
  );
};

export default RatingsReviewsField;
